import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../api/endpoints';
import { Table, type Column, Badge, TableSkeleton, CoreVenueModal, ContextMenu } from '../components/Common';
import type { AdminCoreVenue, AdminCoreVenueUpdate, Address, MonthPeriod, Month } from '../types';
import { externalUrl } from '../utils/externalUrl';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Music, Beer, Guitar, UtensilsCrossed, Hotel, Building2, Tent, Users, Landmark, HelpCircle,
  ExternalLink, PartyPopper, Trophy, GraduationCap, Briefcase, Merge,
} from 'lucide-react';

const SaxophoneIcon = ({ size = 24, className }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M14 2l-4 4 8 8 4-4z" />
    <path d="M10 6L4 12c-2 2-2 5 0 7s5 2 7 0l6-6" />
    <circle cx="8" cy="16" r="1" />
    <circle cx="6" cy="14" r="1" />
    <circle cx="10" cy="18" r="1" />
  </svg>
);

const VENUE_TYPE_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  concert_hall: Music,
  bar: Beer,
  concert_bar: Guitar,
  jazz_club: SaxophoneIcon,
  restaurant: UtensilsCrossed,
  hotel: Hotel,
  city_concert_hall: Building2,
  festival: Tent,
  association: Users,
  city: Landmark,
  comite_des_fetes: PartyPopper,
  competition: Trophy,
  mentoring: GraduationCap,
  booking_agency: Briefcase,
  other: HelpCircle,
};

const MONTH_LABELS: Record<string, string> = {
  january: 'Jan', february: 'Feb', march: 'Mar', april: 'Apr',
  may: 'May', june: 'Jun', july: 'Jul', august: 'Aug',
  september: 'Sep', october: 'Oct', november: 'Nov', december: 'Dec',
};

const MONTH_ORDER: Month[] = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

const formatMonthPeriod = (period: MonthPeriod | undefined): string => {
  if (!period) return '-';
  if (period.all_year) return 'All Year';
  if (period.months.length === 0) return '-';

  const indices = period.months
    .map(m => MONTH_ORDER.indexOf(m))
    .filter(i => i !== -1)
    .sort((a, b) => a - b);

  if (indices.length === 1) {
    return MONTH_LABELS[MONTH_ORDER[indices[0]]];
  }

  const isConsecutive = indices.every((val, i) => i === 0 || val === indices[i - 1] + 1);
  if (isConsecutive) {
    return `${MONTH_LABELS[MONTH_ORDER[indices[0]]]} to ${MONTH_LABELS[MONTH_ORDER[indices[indices.length - 1]]]}`;
  }

  return indices.map(i => MONTH_LABELS[MONTH_ORDER[i]]).join(', ');
};

function getColumns(): Column<AdminCoreVenue>[] {
  return [
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      width: '110px',
      render: (row) => {
        const Icon = VENUE_TYPE_ICONS[row.type || ''] || HelpCircle;
        return (
          <span className="inline-flex items-center gap-1.5 text-xs text-dark-text-secondary">
            <Icon size={14} className="shrink-0" />
            {row.type?.replace('_', ' ') ?? '—'}
          </span>
        );
      },
    },
    {
      key: 'styles',
      label: 'Style',
      width: '110px',
      render: (row) => {
        if (!row.styles || row.styles.length === 0) {
          return <span className="text-dark-text-muted">-</span>;
        }
        return <span className="text-xs text-dark-text-secondary">{row.styles.join(', ')}</span>;
      },
    },
    {
      key: 'name',
      label: 'Venue Name',
      sortable: true,
      width: '200px',
      render: (row) => {
        const inactive = row.status === 'closed' || row.status === 'no_concerts';
        return (
          <div>
            <div className={clsx('font-medium', inactive && 'line-through text-dark-text-muted')}>
              {row.name}
            </div>
            {inactive && (
              <div className="text-xs text-dark-text-muted">
                {row.status === 'closed' ? 'Closed' : 'No Concerts'}
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'address',
      label: 'Address',
      sortable: true,
      width: '160px',
      render: (row) => {
        const address = row.address as Address | undefined;
        if (!address) return <span className="text-dark-text-muted">-</span>;

        const hasStructured = address.city || address.administrative_region_1 || address.administrative_region_2 || address.country;

        if (!hasStructured && address.unparsed_address) {
          return <div className="text-xs text-dark-text-secondary leading-tight">{address.unparsed_address}</div>;
        }

        if (!hasStructured) {
          return <span className="text-dark-text-muted">-</span>;
        }

        return (
          <div className="flex flex-col text-xs leading-tight">
            {address.city && <span className="text-dark-text-primary">{address.city}</span>}
            {address.administrative_region_1 && <span className="text-dark-text-secondary">{address.administrative_region_1}</span>}
            {address.administrative_region_2 && <span className="text-dark-text-muted">{address.administrative_region_2}</span>}
            {address.country && <span className="text-dark-text-muted">{address.country}</span>}
          </div>
        );
      },
    },
    {
      key: 'concert_period',
      label: 'Concert Period',
      width: '100px',
      render: (row) => {
        const text = formatMonthPeriod(row.concert_period);
        if (text === '-') {
          return <span className="text-dark-text-muted">-</span>;
        }
        return <span className="text-xs text-dark-text-secondary">{text}</span>;
      },
    },
    {
      key: 'url',
      label: 'URL',
      sortable: true,
      width: '60px',
      render: (row) => {
        const href = externalUrl(row.url);
        if (!href) return <span className="text-dark-text-muted">-</span>;
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-primary-400 hover:text-primary-300 transition-colors"
            title={row.url}
          >
            <ExternalLink size={14} />
          </a>
        );
      },
    },
    {
      key: 'visibility',
      label: 'Visibility',
      sortable: true,
      width: '100px',
      render: (row) => (
        <span className={clsx(
          'text-xs font-medium',
          row.visibility === 'public' && 'text-emerald-400',
          row.visibility === 'pending_public' && 'text-amber-400',
          row.visibility === 'refused_public' && 'text-red-400',
          row.visibility === 'private' && 'text-dark-text-muted',
        )}>
          {row.visibility.replace(/_/g, ' ')}
        </span>
      ),
    },
    {
      key: 'owner_email',
      label: 'Owner',
      sortable: true,
      width: '180px',
      render: (row) => (
        <span className="text-xs text-dark-text-secondary">{row.owner_email || row.owner_user_id}</span>
      ),
    },
  ];
}

export function DisambiguatePage() {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mergeTarget, setMergeTarget] = useState<AdminCoreVenue | null>(null);
  const [mergeSources, setMergeSources] = useState<AdminCoreVenue[]>([]);
  const [contextMenuState, setContextMenuState] = useState<{ venue: AdminCoreVenue; position: { x: number; y: number } } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'core-venues'],
    queryFn: () => adminAPI.getCoreVenues().then((r) => r.data),
  });

  const updateMutation = useMutation({
    mutationFn: ({ venueId, data }: { venueId: string; data: AdminCoreVenueUpdate }) =>
      adminAPI.updateCoreVenue(venueId, data),
  });

  const mergeMutation = useMutation({
    mutationFn: ({ targetId, sourceIds }: { targetId: string; sourceIds: string[] }) =>
      adminAPI.mergeCoreVenues(targetId, sourceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'core-venues'] });
    },
  });

  // Flatten all venues into a single list
  const allVenues = useMemo(() => [
    ...(data?.pending ?? []),
    ...(data?.public ?? []),
    ...(data?.refused ?? []),
  ], [data]);

  const columns = getColumns();

  const toggleSelection = useCallback((venue: AdminCoreVenue, e: React.MouseEvent) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (e.shiftKey || e.ctrlKey || e.metaKey) {
        // Multi-select: toggle
        if (next.has(venue.id)) {
          next.delete(venue.id);
        } else {
          next.add(venue.id);
        }
      } else {
        // Single click: toggle this one, clear others
        if (next.has(venue.id) && next.size === 1) {
          next.clear();
        } else {
          next.clear();
          next.add(venue.id);
        }
      }
      return next;
    });
  }, []);

  const handleCellRightClick = useCallback((row: AdminCoreVenue, _columnKey: string, position: { x: number; y: number }) => {
    setContextMenuState({ venue: row, position });
  }, []);

  const getContextMenuItems = useCallback(() => {
    if (!contextMenuState) return [];
    const venue = contextMenuState.venue;

    // Ensure the right-clicked row is included in selection
    const effectiveSelectedIds = new Set(selectedIds);
    effectiveSelectedIds.add(venue.id);

    const sourceIds = new Set(effectiveSelectedIds);
    sourceIds.delete(venue.id);

    const sourceVenues = allVenues.filter(v => sourceIds.has(v.id));
    const hasOtherSelected = sourceVenues.length > 0;

    return [
      {
        label: hasOtherSelected
          ? `Merge ${sourceVenues.length} venue${sourceVenues.length > 1 ? 's' : ''} into this`
          : 'Merge into (select other venues first)',
        icon: <Merge size={14} />,
        disabled: !hasOtherSelected,
        onClick: () => {
          setSelectedIds(effectiveSelectedIds);
          setMergeTarget(venue);
          setMergeSources(sourceVenues);
        },
      },
    ];
  }, [contextMenuState, selectedIds, allVenues]);

  const handleMerge = async (venueId: string, updateData: AdminCoreVenueUpdate, hasChanges: boolean) => {
    // Update target venue if changes were made
    if (hasChanges) {
      await updateMutation.mutateAsync({ venueId, data: updateData });
    }

    const sourceIds = mergeSources.map(v => v.id);
    const result = await mergeMutation.mutateAsync({ targetId: venueId, sourceIds });

    const r = result.data;
    toast.success(
      `Merged ${r.sources_deleted} venue${r.sources_deleted > 1 ? 's' : ''}. ` +
      `Notes: ${r.notes_reassigned} reassigned, ${r.notes_merged} merged. ` +
      `Contacts: ${r.contacts_updated}, Concerts: ${r.concerts_updated}.` +
      (r.errors.length > 0 ? ` Warnings: ${r.errors.join(', ')}` : ''),
      { duration: 6000 }
    );

    setSelectedIds(new Set());
    setMergeTarget(null);
    setMergeSources([]);
  };

  const handleModalSubmit = async (venueId: string, updateData: AdminCoreVenueUpdate) => {
    await updateMutation.mutateAsync({ venueId, data: updateData });
    queryClient.invalidateQueries({ queryKey: ['admin', 'core-venues'] });
  };

  return (
    <div className="h-full flex flex-col animate-fadeIn">
      <div className="max-w-(--breakpoint-xl) mx-auto w-full p-6 flex-1 flex flex-col min-h-0 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-dark-text-primary tracking-tight">Disambiguate</h1>
            <Badge variant="success">{allVenues.length}</Badge>
          </div>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-dark-text-secondary">
                {selectedIds.size} selected
              </span>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-dark-text-muted hover:text-dark-text-secondary transition-colors"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        <p className="text-sm text-dark-text-muted">
          Select venues with Ctrl/Cmd+click, then right-click on the target venue to merge.
        </p>

        {isLoading ? (
          <div className="card">
            <TableSkeleton rows={10} />
          </div>
        ) : (
          <div className="card animate-scaleIn flex flex-col min-h-0">
            {allVenues.length === 0 ? (
              <div className="px-4 py-8 text-center text-dark-text-muted">
                No core venues
              </div>
            ) : (
              <Table
                data={allVenues}
                columns={columns}
                searchable={true}
                searchPlaceholder="Search venues by name, address, url..."
                emptyMessage="No venues match your search"
                onRowClick={(row, e) => toggleSelection(row, e)}
                onCellRightClick={handleCellRightClick}
                rowClassName={(row) =>
                  selectedIds.has(row.id)
                    ? 'bg-primary-900/20 hover:bg-primary-900/30'
                    : undefined
                }
              />
            )}
          </div>
        )}
      </div>

      <CoreVenueModal
        isOpen={mergeTarget !== null}
        onClose={() => { setMergeTarget(null); setMergeSources([]); }}
        onSubmit={handleModalSubmit}
        venue={mergeTarget}
        mergeSourceNames={mergeSources.map(v => v.name)}
        onMerge={handleMerge}
      />

      {contextMenuState && (
        <ContextMenu
          items={getContextMenuItems()}
          position={contextMenuState.position}
          onClose={() => setContextMenuState(null)}
        />
      )}
    </div>
  );
}
